import { Injectable, OnDestroy, computed, effect, inject } from '@angular/core';
import { Subscription, map } from 'rxjs';
import { UnassignedTenantsAuthError } from 'src/app/auth/models/auth-error';
import { TenantService } from 'src/app/tenant/services/tenant.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { IPageFilterSort, IPagingResponse, PagingResponse, SortOrder } from 'src/app/shared/models/api';
import { ICreateBulkCardHolderAndCardCredentialsDTO, IMoveLockersToLockerBankDTO } from 'src/app/shared/models/bulk';
import { ICardCredentialDTO, ICreateCardCredentialDTO, IUpdateCardCredentialDTO } from 'src/app/shared/models/card-credential';
import { ICardHolderAndCardCredentialsDTO } from 'src/app/shared/models/card-credential-and-card-holder';
import { ICardHolderDTO, ICreateCardHolderDTO, IUpdateCardHolderDTO } from 'src/app/shared/models/card-holder';
import { EntityId } from 'src/app/shared/models/common';
import { IGlobalLockerSearchResultDTO } from 'src/app/shared/models/global-locker-search-result';
import { ICreateLocationDTO, ILocationDTO, IUpdateLocationDTO } from 'src/app/shared/models/location';
import { ICreateLockDTO, ILockDTO, ILockListItemDTO, ILockWithMetricsDTO, IUpdateLockDTO, LockConnectionStatus } from 'src/app/shared/models/lock';
import { ILockConfigStatusDTO } from 'src/app/shared/models/lock-config-status';
import { ILockEventAuditDTO } from 'src/app/shared/models/lock-event-audit';
import { ICreateLockerDTO, ILockerDTO, IUpdateLockerDTO } from 'src/app/shared/models/locker';
import { ICreateLockerAndLockDTO, ILockerAndLockDTO } from 'src/app/shared/models/locker-and-lock';
import { ILockerAuditDTO } from 'src/app/shared/models/locker-audit';
import { ICreateLockerBankDTO, ILockerBankDTO, IUpdateLockerBankDTO } from 'src/app/shared/models/locker-bank';
import { ILockerBankAdminDTO } from 'src/app/shared/models/locker-bank-admin';
import { ICreateLockerCardCredentialDTO } from 'src/app/shared/models/locker-card-credential';
import { ICreateLockerFaultDTO, ILockerFaultDTO, IUpdateLockerFaultDTO } from 'src/app/shared/models/locker-fault';
import { ILockerLeaseDTO } from 'src/app/shared/models/locker-lease';
import { ILockerStatusDTO } from 'src/app/shared/models/locker-status';
import { ICreateReferenceImageDTO, IUpdateReferenceImageDTO, IViewReferenceImageDTO } from 'src/app/shared/models/reference-image';
import { ICreateSystemUpdateNotificationDTO, ISystemUpdateNotificationDTO } from 'src/app/shared/models/system-update-notification';
import { ICreateTenantDTO, ITenantDTO, IUpdateTenantDTO, TenantBrandingDTO, UpdateTenantBrandingDTO } from 'src/app/shared/models/tenant';
import { ICreateSlcDTO, ISlcDashboardDTO, ISlcDTO, ISlcManagedLockDTO, ISlcTopologyDTO, IUpdateSlcDTO } from 'src/app/shared/models/slc';
import { ICreateHidFormatConfigDTO, IHidFormatConfigDTO, IHidFormatTemplateDTO, IUpdateHidFormatConfigDTO } from 'src/app/shared/models/hid-format-config';
import { ITenantCapabilitiesDTO } from 'src/app/shared/models/tenant-capabilities';
import { ApiService } from 'src/app/shared/services/api.service';
import { ILockerBankAdminLocationSummaryDTO } from 'src/app/v2/shared/models/location-locker-bank-summary';
import { ILockerWithKPIDTO } from 'src/app/v2/shared/models/locker-with-kpi';

@Injectable({
  providedIn: 'root'
})
export class AdminApiService {

  private readonly apiService = inject(ApiService);
  private readonly tenantService = inject(TenantService);

  private readonly tenantId = computed(() => {
    const tenantDetails = this.tenantService.tenantDetails();
    return tenantDetails?.id ?? null;
  });

  // API Endpoints
  Tenants = this.apiService.generateCrudEndpoints<ITenantDTO, ICreateTenantDTO, IUpdateTenantDTO>(this.endpointFor());

  TenantCapabilities = {
    get: (tenantId: EntityId) => this.apiService.get<ITenantCapabilitiesDTO>(`/tenants/${tenantId}/capabilities`).pipe(map(x => x?.data ?? null)),
  };

  private readonly locationsEndpoint = this.endpointFor('locations');
  private readonly lockerBanksEndpoint = this.endpointFor('locker-banks');
  private readonly lockersEndpoint = this.endpointFor('lockers');
  private readonly locksEndpoint = this.endpointFor('locks');
  private readonly cardHoldersEndpoint = this.endpointFor('card-holders');
  private readonly lockerLeasesEndpoint = this.endpointFor('locker-leases');
  private readonly locationsReferenceImagesEndpoint = this.endpointFor('locations/reference-images');
  private readonly lockerBanksReferenceImagesEndpoint = this.endpointFor('locker-banks/reference-images');
  private readonly lockerFaultsEndpoint = this.endpointFor('lockers/locker-faults');
  private readonly slcsEndpoint = this.endpointFor('slcs');


  Locations = {
    ...this.apiService.generateCrudEndpoints<ILocationDTO, ICreateLocationDTO, IUpdateLocationDTO>(this.locationsEndpoint),
    LockerBanks: this.apiService.generateNestedResource<ILockerBankDTO, ICreateLockerBankDTO>(this.locationsEndpoint, 'locker-banks'),
    Lockers: {
      Statuses: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerStatusDTO>(this.locationsEndpoint, 'lockers-status'),
    },
    ReferenceImages: {
      ...this.apiService.generateCrudEndpoints<IViewReferenceImageDTO, ICreateReferenceImageDTO, IUpdateReferenceImageDTO>(this.locationsReferenceImagesEndpoint),
    },
  };

  LockerBanks = {
    ...this.apiService.generateCrudEndpoints<ILockerBankDTO, ICreateLockerBankDTO, IUpdateLockerBankDTO>(this.lockerBanksEndpoint),
    Lockers: {
      ...this.apiService.generateNestedResource<ILockerDTO, ICreateLockerDTO>(this.lockerBanksEndpoint, 'lockers'),
      Statuses: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerStatusDTO>(this.lockerBanksEndpoint, 'lockers-status'),
      WithKPIs: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerWithKPIDTO>(this.lockerBanksEndpoint, 'lockers-kpi'),
      Move: this.apiService.generateNestedUpdateManyWithResult<IMoveLockersToLockerBankDTO, ILockerDTO[]>(this.lockerBanksEndpoint, 'lockers'),
    },
    LockersWithLocks: {
      ...this.apiService.generateNestedResource<ILockerAndLockDTO, ICreateLockerAndLockDTO>(this.lockerBanksEndpoint, 'lockers-and-locks'),
    },
    Admins: {
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ICardHolderDTO>(this.lockerBanksEndpoint, 'admins'),
      updateMany: this.apiService.generateNestedUpdateMany<EntityId>(this.lockerBanksEndpoint, 'admins'),
      deleteSingle: this.apiService.generateNestedDelete(this.lockerBanksEndpoint, 'admins'),
    },
    SpecialCards: {
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ICardHolderAndCardCredentialsDTO>(this.lockerBanksEndpoint, 'special-cards'),
      updateMany: this.apiService.generateNestedUpdateMany<EntityId>(this.lockerBanksEndpoint, 'special-cards'),
    },
    LeaseUsers: {
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ICardHolderAndCardCredentialsDTO>(this.lockerBanksEndpoint, 'lease-users'),
      updateMany: this.apiService.generateNestedUpdateMany<EntityId>(this.lockerBanksEndpoint, 'lease-users'),
    },
    ReferenceImages: {
      ...this.apiService.generateCrudEndpoints<IViewReferenceImageDTO, ICreateReferenceImageDTO, IUpdateReferenceImageDTO>(this.lockerBanksReferenceImagesEndpoint),
    },
  };

  Lockers = {
    ...this.apiService.generateCrudEndpoints<ILockerDTO, ICreateLockerDTO, IUpdateLockerDTO>(this.lockersEndpoint),
    // Lock: this.apiService.generateGetSingle<ILockDTO>(this.lockersEndpoint, 'lock'),
    Audits: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerAuditDTO>(this.lockersEndpoint, 'audits'),
    WithLocationsAndLockerBanksAndLocks: this.apiService.generatePageFilterSortRequestWithCustomReturn<IGlobalLockerSearchResultDTO>(this.endpointFor('lockers/with-location-locker-bank-lock')),
    WithStatus: this.apiService.generatePageFilterSortRequest<ILockerStatusDTO>(this.endpointFor('lockers/with-status')),
    Leases: {
      getSingle: this.apiService.generateGetSingle<ILockerLeaseDTO>(this.endpointFor('lockers/leases')),
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerLeaseDTO>(this.lockersEndpoint, 'leases'),
    },
    Owners: {
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ICardHolderAndCardCredentialsDTO>(this.lockersEndpoint, 'owners'),
      updateManyForSmartLocker: this.apiService.generateNestedUpdateMany<EntityId>(this.lockersEndpoint, 'owners/card-credentials'),
      updateManyForDumbLocker: this.apiService.generateNestedUpdateMany<EntityId>(this.lockersEndpoint, 'owners/card-holders'),
    },
    Faults: {
      ...this.apiService.generateCrudEndpoints<ILockerFaultDTO, ICreateLockerFaultDTO, IUpdateLockerFaultDTO>(this.lockerFaultsEndpoint),
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerFaultDTO>(this.lockersEndpoint, 'locker-faults'),
    },
    Conversion: {
      getCompatibility: (lockerId: EntityId) => this.apiService.get<ICardHolderDTO[]>(this.lockersEndpoint, { requestSettings: { urlFragments: [lockerId, 'convert-locker'] } }).pipe(map(x => x?.data)),
      patchConvertToSmart: (lockerId: EntityId, body: { newLockId: EntityId | null }) => this.apiService.patch(this.lockersEndpoint, { body, requestSettings: { urlFragments: [lockerId, 'convert-locker'] } }),
    }
  };

  Locks = {
    ...this.apiService.generateCrudEndpoints<ILockDTO, ICreateLockDTO, IUpdateLockDTO>(this.locksEndpoint),
    getManyWithMetrics: (baseFilter: IPageFilterSort, filters?: {
      vendor?: number;
      operatingMode?: number;
      locationId?: EntityId;
      lockerBankId?: EntityId;
      slcId?: EntityId;
      hasLocker?: boolean;
      status?: LockConnectionStatus;
      batteryIsLow?: boolean;
      hasAlerts?: boolean;
      searchText?: string;
    }) => {
      // Build query params for the unified metrics endpoint with correct parameter names
      const queryParams = new URLSearchParams();
      
      // Pagination - transform to new format
      if (baseFilter.page) {
        queryParams.append('pageSize', baseFilter.page.recordsPerPage.toString());
        queryParams.append('page', (baseFilter.page.pageIndex + 1).toString()); // Convert 0-based to 1-based
      }
      
      // Sorting - transform to numeric sortOrder
      if (baseFilter.sort?.sortBy) {
        queryParams.append('sortBy', baseFilter.sort.sortBy);
        const sortOrderNumeric = baseFilter.sort.sortOrder === SortOrder.Ascending ? 0 : 1;
        queryParams.append('sortOrder', sortOrderNumeric.toString());
      }
      
      // Search text - use searchText instead of filterValue/filterProperties
      const searchText = filters?.searchText || baseFilter.filter?.filterValue;
      if (searchText) {
        queryParams.append('searchText', searchText);
      }
      
      // Additional filters
      if (filters) {
        if (filters.vendor !== undefined) queryParams.append('vendor', filters.vendor.toString());
        if (filters.operatingMode !== undefined) queryParams.append('operatingMode', filters.operatingMode.toString());
        if (filters.locationId) queryParams.append('locationId', filters.locationId);
        if (filters.lockerBankId) queryParams.append('lockerBankId', filters.lockerBankId);
        if (filters.slcId) queryParams.append('slcId', filters.slcId);
        if (filters.hasLocker !== undefined) queryParams.append('hasLocker', filters.hasLocker.toString());
        if (filters.status !== undefined) queryParams.append('status', filters.status.toString());
        if (filters.batteryIsLow !== undefined) queryParams.append('batteryIsLow', filters.batteryIsLow.toString());
        if (filters.hasAlerts !== undefined) queryParams.append('hasAlerts', filters.hasAlerts.toString());
      }
      
      // Make direct HTTP call to /with-metrics endpoint
      return this.apiService.get<IPagingResponse<ILockWithMetricsDTO>>(
        (baseUrl: string) => `${this.locksEndpoint(baseUrl)}/with-metrics?${queryParams.toString()}`
      ).pipe(map(response => response?.data ?? new PagingResponse<ILockWithMetricsDTO>()));
    },
    getSingleWithMetrics: (lockId: EntityId) =>
      this.apiService.get<ILockWithMetricsDTO>(
        (baseUrl: string) => `${this.locksEndpoint(baseUrl)}/${lockId}/with-metrics`
      ).pipe(map(x => x?.data ?? null)),
    partialUpdateSingle: (lockId: EntityId, body: { lockerId: EntityId | null }) => this.apiService.patch(this.locksEndpoint, { body, requestSettings: { urlFragments: [lockId] } }),
    ForceConfigUpdate: (lockId: EntityId) => this.apiService.put(this.locksEndpoint, { requestSettings: { urlFragments: [lockId, 'config'] } }),
    IsConfigPending: (lockId: EntityId) => this.apiService.get<ILockConfigStatusDTO>(this.locksEndpoint, { requestSettings: { urlFragments: [lockId, 'config-pending'] } }).pipe(map(x => x?.data)),
    LockUpdateHistory:
    {
      getMany: this.apiService.generateNestedResourcePageFilterSortRequest<ILockEventAuditDTO>(this.locksEndpoint, 'lock-update-history'),
    }
  };

  CardHolders = {
    ...this.apiService.generateCrudEndpoints<ICardHolderDTO, ICreateCardHolderDTO, IUpdateCardHolderDTO>(this.cardHoldersEndpoint),
    LockerLeases: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerLeaseDTO>(this.cardHoldersEndpoint, 'locker-leases'),
    CardCredentials: 
    {
      ...this.apiService.generateNestedResource<ICardCredentialDTO, ICreateLockerCardCredentialDTO>(this.cardHoldersEndpoint, 'card-credentials')
    },
    LockerStatus: (cardHolderId: EntityId) => this.apiService.get<ILockerStatusDTO>(this.cardHoldersEndpoint, { requestSettings: { urlFragments: [cardHolderId, 'locker-status'] } }).pipe(map(x => x?.data)),
    WithUserCardCredentials: this.apiService.generatePageFilterSortRequest<ICardHolderAndCardCredentialsDTO>(this.endpointFor('card-holders/with-user-card-credentials')),
    WithSpecialCardCredentials: this.apiService.generatePageFilterSortRequest<ICardHolderAndCardCredentialsDTO>(this.endpointFor('card-holders/with-special-card-credentials')),
  };

  CardCredentials = {
    ...this.apiService.generateCrudEndpoints<ICardCredentialDTO, ICreateCardCredentialDTO, IUpdateCardCredentialDTO>(this.endpointFor('card-credentials')),
  };

  HidFormatConfigs = {
    ...this.apiService.generateCrudEndpoints<IHidFormatConfigDTO, ICreateHidFormatConfigDTO, IUpdateHidFormatConfigDTO>(this.endpointFor('hid-format-configs')),
    getTemplates: () => this.apiService.get<IHidFormatTemplateDTO[]>(this.endpointFor('hid-format-configs/templates')).pipe(map(x => x?.data ?? [])),
  };

   TenantBranding = {
      getBranding: (tenantId: EntityId) => this.apiService.get<TenantBrandingDTO>((baseUrl) => `${baseUrl}/tenants/${tenantId}/branding`).pipe(map(x => x?.data)),
      updateBranding: (tenantId: EntityId, body: UpdateTenantBrandingDTO) => this.apiService.put<UpdateTenantBrandingDTO, EntityId>((baseUrl) => `${baseUrl}/tenants/${tenantId}/branding`, { body })
   };
  
  LockerBankAdmins = {
    getMany: this.apiService.generatePageFilterSortRequest<ILockerBankAdminDTO>(this.endpointFor('locker-bank-admins')),
    Locations: {
      getMany: this.apiService.generatePageFilterSortRequest<ILocationDTO>(this.endpointFor('locker-bank-admins/locations')),
      LockerBanks: this.apiService.generateNestedResourcePageFilterSortRequest<ILockerBankDTO>(this.endpointFor('locker-bank-admins/locations'), 'locker-banks')
    },
    LockerBanks: this.apiService.generatePageFilterSortRequest<ILockerBankDTO>(this.endpointFor('locker-bank-admins/locker-banks')),
    LockerBankSummaries: this.apiService.generatePageFilterSortRequest<ILockerBankAdminLocationSummaryDTO>(this.endpointFor('locker-bank-admins/locker-bank-summaries')),
    LockerNotes: {
      partialUpdateSingle: (lockerId: EntityId, body: { notes: string | undefined }) => this.apiService.patch(this.endpointFor('locker-bank-admins/locker-notes'), { body, requestSettings: { urlFragments: [lockerId] } }),
    }
  };

  LockerLeases = {
    getMany: this.apiService.generatePageFilterSortRequest<ILockerLeaseDTO>(this.lockerLeasesEndpoint),
  };

  BulkOperations = {
    createManyCardHolderAndCardCredentialPairs: this.apiService.generateCreateSingle<null, ICreateBulkCardHolderAndCardCredentialsDTO>(this.endpointFor('bulk-operations/card-holder-and-credentials')),
  };

  AccessControl = {
    getCredentialFromAccessControl: this.apiService.generateGetSingle<ICardCredentialDTO>(this.endpointFor('access-control/card-holder/credentials')),
  }

  SystemUpdateNotification = {
    getSingle: this.apiService.generateGetSingleParamaterless<ISystemUpdateNotificationDTO>('/systemupdatenotification'),
    createSingle: this.apiService.generateCreateSingle<ISystemUpdateNotificationDTO, ICreateSystemUpdateNotificationDTO>('/systemupdatenotification'),
  }

  Slcs = {
    ...this.apiService.generateCrudEndpoints<ISlcDTO, ICreateSlcDTO, IUpdateSlcDTO>(this.slcsEndpoint),
    getMany: this.apiService.generatePageFilterSortRequest<ISlcDashboardDTO>(this.slcsEndpoint),
    getManagedLocks: (slcId: EntityId) => 
      this.apiService.get<ISlcManagedLockDTO[]>((baseUrl: string) => `${this.slcsEndpoint(baseUrl)}/${slcId}/managed-locks`).pipe(map(x => x?.data ?? [])),
    getTopology: (slcId: EntityId) =>
      this.apiService.get<ISlcTopologyDTO>((baseUrl: string) => `${this.slcsEndpoint(baseUrl)}/${slcId}/topology`).pipe(map(x => x?.data ?? null)),
  };

  // NOTE: Requires i18n
  private readonly errorMessageNoTenantSelected = 'You do not have a tenant selected. Please select a tenant and try again';

  private endpointFor(endpoint?: string): ((baseUrl: string) => string) {
    return baseUrl => {
      if(endpoint) {
        const currentTenantId = this.tenantId();
        if(!currentTenantId) {
          // throw new UnassignedTenantsAuthError(this.errorMessageNoTenantSelected);
        }
        return `${baseUrl}/tenants/${currentTenantId}/${endpoint}`;
      }
      return `${baseUrl}/tenants`;
    }
  }
}

